import React, { useEffect, useRef, useState } from 'react'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { billingQueue as urls } from '@URLS'
import { toast } from '@CommonComponents/toast'
import { LoadingSpinner } from '@CommonComponents'
import { Pagination } from '@CommonComponents/buttons'
import { Filters } from './Filters'
import { QueueItem } from './QueueItem'

const PAGE_SIZE = 20

export const QueueTab = () => {
  const [state, dispatch] = useStateValue()
  const { queue, filters, schools } = state
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const searchTimer = useRef(null)

  const fetchQueue = async (currentFilters) => {
    setLoading(true)
    try {
      const resp = await client.get(
        urls.getQueue({ filter: currentFilters.type !== 'all' ? currentFilters.type : undefined, search: currentFilters.search || undefined })
      )
      dispatch({ type: 'SET_QUEUE', payload: resp.data.items })
    } catch {
      toast.error('Failed to load billing queue.')
    }
    setLoading(false)
  }

  const fetchSchools = async () => {
    if (schools.length > 0) return
    try {
      const resp = await client.get(urls.getSchools())
      dispatch({ type: 'SET_SCHOOLS', payload: resp.data.schools })
    } catch {
      // non-critical
    }
  }

  useEffect(() => {
    fetchSchools()
    fetchQueue(filters)
  }, [])

  const handleFilterChange = newType => {
    const newFilters = { ...filters, type: newType }
    dispatch({ type: 'SET_FILTERS', payload: { type: newType } })
    setCurrentPage(1)
    fetchQueue(newFilters)
  }

  const handleSearchChange = newSearch => {
    const newFilters = { ...filters, search: newSearch }
    dispatch({ type: 'SET_FILTERS', payload: { search: newSearch } })
    setCurrentPage(1)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => fetchQueue(newFilters), 300)
  }

  // Confirming the last item on the last page shrinks the queue under the
  // pager, so show the last page that still has items.
  const page = Math.min(
    currentPage,
    Math.max(1, Math.ceil(queue.length / PAGE_SIZE))
  )

  const emptyMessage = () => {
    if (filters.search) {
      return {
        icon: 'fa-search',
        title: `No courses match "${filters.search}"`,
        sub: 'Try a different search term.',
      }
    }
    if (filters.type === 'deferred') {
      return {
        icon: 'fa-clock',
        title: 'No deferred courses',
        sub: 'Courses you defer will appear here for later review.',
      }
    }
    return {
      icon: 'fa-check-circle',
      title: 'Queue is clear',
      sub: 'All courses have been classified.',
    }
  }

  return (
    <div>
      <Filters
        activeFilter={filters.type}
        search={filters.search}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
      />

      {loading ? (
        <div className="text-center py-5">
          <LoadingSpinner />
        </div>
      ) : queue.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            {(() => {
              const msg = emptyMessage()
              return (
                <>
                  <i className={`fa-regular ${msg.icon} fa-2x text-muted mb-3`} />
                  <h5 className="text-muted">{msg.title}</h5>
                  <p className="text-muted small mb-0">{msg.sub}</p>
                </>
              )
            })()}
          </div>
        </div>
      ) : (
        <div>
          {queue.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(br => (
            <QueueItem
              key={br.id}
              br={br}
            />
          ))}
          {Math.ceil(queue.length / PAGE_SIZE) > 1 && (
            <Pagination
              currentPage={page}
              rate={PAGE_SIZE}
              total={queue.length}
              setCurrentPage={setCurrentPage}
            />
          )}
        </div>
      )}

    </div>
  )
}
